import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { Loader2, ExternalLink, BookOpen, Copy, Check, X, AlertCircle } from 'lucide-react';
import { buildYouVersionLink } from '../utils/bibleReferences';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;
const DEFAULT_BIBLE_ID = 147; // Reina-Valera Antigua (español)

const BibleRefViewerContext = createContext({ open: () => {} });
export const useBibleRefViewer = () => useContext(BibleRefViewerContext);

export const BibleRefViewerProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [usfm, setUsfm] = useState('');
  const [label, setLabel] = useState('');
  const [bibleId, setBibleId] = useState(() => {
    const stored = Number(localStorage.getItem('yv_last_bible'));
    return Number.isFinite(stored) && stored > 0 ? stored : DEFAULT_BIBLE_ID;
  });
  const [passage, setPassage] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [bibles, setBibles] = useState([]);

  const openRef = useCallback((targetUsfm, targetLabel = '') => {
    if (!targetUsfm) return;
    setUsfm(targetUsfm);
    setLabel(targetLabel || targetUsfm);
    setPassage(null);
    setErrorMsg('');
    setOpen(true);
  }, []);

  const close = useCallback(() => setOpen(false), []);

  // Lock body scroll while modal is open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Cerrar con ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, close]);

  // Load bibles list once when modal opens
  useEffect(() => {
    if (!open || bibles.length > 0) return;
    axios
      .get(`${API}/youversion/biblias`)
      .then((res) => setBibles(res.data?.data || []))
      .catch((e) => console.warn('BibleRefViewer: bibles list error', e?.response?.status));
  }, [open, bibles.length]);

  // Fetch passage
  useEffect(() => {
    if (!open || !usfm) return;
    setLoading(true);
    setPassage(null);
    setErrorMsg('');
    const url = `${API}/youversion/biblias/${bibleId}/pasaje?ref=${encodeURIComponent(usfm)}&format=html`;
    axios
      .get(url, { timeout: 15000 })
      .then((res) => {
        if (res?.data?.content) {
          setPassage(res.data);
        } else {
          setErrorMsg('No se encontró el versículo en esta versión.');
        }
      })
      .catch((err) => {
        console.error('BibleRefViewer fetch error', { url, status: err?.response?.status, detail: err?.response?.data });
        const status = err?.response?.status;
        if (status === 404) setErrorMsg(`"${label}" no está disponible en esta versión.`);
        else if (status === 401) setErrorMsg('Tu sesión expiró. Iniciá sesión otra vez.');
        else setErrorMsg(err?.response?.data?.detail || 'No se pudo cargar el versículo. Probá de nuevo.');
      })
      .finally(() => setLoading(false));
  }, [open, usfm, bibleId, label]);

  const handleCopy = async () => {
    if (!passage?.content) return;
    const plain = String(passage.content).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    try {
      await navigator.clipboard.writeText(`${passage.reference || label}\n\n${plain}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* ignore */ }
  };

  const currentBible = bibles.find((b) => b.id === bibleId);

  return (
    <BibleRefViewerContext.Provider value={{ open: openRef }}>
      {children}
      {open && (
        <div
          className="fixed inset-0 z-[9999] bg-black/50 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6"
          onClick={close}
          data-testid="bible-ref-viewer-backdrop"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[calc(100dvh-2rem)] flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="bible-ref-viewer"
          >
            {/* Header: limpio, sin gradiente fuerte. Misma línea visual que el ping modal */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="w-9 h-9 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-bold text-gray-900 text-sm sm:text-base truncate">
                    {passage?.reference || label}
                  </h3>
                  {currentBible && (
                    <p className="text-[11px] text-gray-400 truncate">
                      {currentBible.localized_title || currentBible.title}
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={close}
                className="text-gray-400 hover:text-gray-700 transition flex-shrink-0 ml-2"
                aria-label="Cerrar"
                data-testid="bible-ref-viewer-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Version selector */}
            {bibles.length > 1 && (
              <div className="px-5 pt-3 flex items-center gap-2 flex-wrap">
                <span className="text-[11px] text-gray-500 font-medium">Versión:</span>
                <select
                  value={bibleId}
                  onChange={(e) => {
                    const newId = Number(e.target.value);
                    setBibleId(newId);
                    localStorage.setItem('yv_last_bible', String(newId));
                  }}
                  className="text-xs px-2 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:outline-none bg-white"
                  data-testid="bible-ref-viewer-version-select"
                >
                  {bibles.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.localized_abbreviation || b.abbreviation}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Body */}
            <div className="p-5 overflow-y-auto flex-1">
              {loading && (
                <div className="py-10 text-center">
                  <Loader2 className="w-7 h-7 animate-spin text-amber-500 mx-auto" />
                  <p className="text-xs text-gray-400 mt-2">Cargando versículo...</p>
                </div>
              )}

              {!loading && errorMsg && (
                <div className="py-8 text-center" data-testid="bible-ref-viewer-error">
                  <AlertCircle className="w-8 h-8 text-amber-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-700">{errorMsg}</p>
                  {bibles.length > 1 && (
                    <p className="text-xs text-gray-400 mt-2">Probá con otra versión arriba.</p>
                  )}
                </div>
              )}

              {!loading && !errorMsg && passage?.content && (
                <article
                  className="prose prose-sm max-w-none font-serif leading-relaxed text-gray-800 yv-passage"
                  dangerouslySetInnerHTML={{ __html: passage.content }}
                  data-testid="bible-ref-viewer-content"
                />
              )}

              {currentBible?.copyright && passage?.content && (
                <p className="text-[10px] text-gray-400 mt-4 text-center italic">
                  {currentBible.copyright}
                </p>
              )}
            </div>

            {/* Footer */}
            {passage?.content && !errorMsg && (
              <div className="flex items-center gap-2 px-5 py-3 border-t border-gray-100 bg-gray-50">
                <button
                  onClick={handleCopy}
                  className="text-xs text-gray-700 hover:text-amber-700 flex items-center gap-1.5 px-3 py-1.5 rounded-md hover:bg-white border border-transparent hover:border-gray-200 transition"
                  data-testid="bible-ref-viewer-copy"
                >
                  {copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? 'Copiado' : 'Copiar'}
                </button>
                <a
                  href={buildYouVersionLink(usfm, bibleId, currentBible?.abbreviation || 'RVES')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-gray-700 hover:text-amber-700 flex items-center gap-1.5 px-3 py-1.5 rounded-md hover:bg-white border border-transparent hover:border-gray-200 transition ml-auto"
                  data-testid="bible-ref-viewer-yv-link"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  Abrir en YouVersion
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </BibleRefViewerContext.Provider>
  );
};
